import { toast } from "react-toastify";
import { t } from "i18next";
import { printBase64Pdf } from "./qzTray";

/** A4 document or the narrow layout sent to the receipt printer. */
export type ReceiptFormat = "a4" | "thermal";

/** What a payment/wallet receipt covers. */
export type ReceiptSource =
	| { kind: "payments"; ids: number[] }
	| { kind: "invoice"; invoice: string; reference?: string }
	| { kind: "wallet"; id: number };

type Api = {
	get: (url: string, config?: any) => Promise<any>;
	post: (url: string, body?: any, config?: any) => Promise<any>;
};

/** Open a base64 PDF returned by the API in a new tab. */
export function openPdfBase64(data: string): void {
	const pdfData = atob(data);
	const blob = new Blob([Uint8Array.from(pdfData, (c) => c.charCodeAt(0))], { type: "application/pdf" });
	window.open(URL.createObjectURL(blob));
}

function fetchReceipt(api: Api, source: ReceiptSource, format: ReceiptFormat | "json", config?: any) {
	if (source.kind === "wallet") {
		return api.get(`/receipts/wallet/${source.id}`, { ...config, params: { format } });
	}
	const body = source.kind === "payments" ? { ids: source.ids, format } : { invoice: source.invoice, format };
	return api.post("/receipts/payments", body, config);
}

/** The receipt's content as JSON, for the on-screen preview. Errors are shown inline, not toasted. */
export function fetchReceiptPreview(api: Api, source: ReceiptSource) {
	return fetchReceipt(api, source, "json", { skipErrorToast: true });
}

/**
 * Open (A4) or print (thermal, through QZ Tray) a payment or wallet receipt.
 * Backend refusals (mixed clients, refunds...) are toasted by the API client.
 */
export async function issueReceipt(
	api: Api,
	source: ReceiptSource,
	format: ReceiptFormat,
	setLoadingMessage: (message: string | null) => void,
): Promise<boolean> {
	if (format === "thermal") {
		return printBase64Pdf(() => fetchReceipt(api, source, "thermal"), setLoadingMessage);
	}
	setLoadingMessage("EXPORT");
	try {
		const res = await fetchReceipt(api, source, "a4");
		if (res?.status === 200 && res.data?.data) {
			openPdfBase64(res.data.data);
			return true;
		}
		if (!res || res.status >= 500) toast.error(t("common:report.failedToGenerate"));
		return false;
	} finally {
		setLoadingMessage(null);
	}
}
